import React from 'react';
import { ReviewQueueEntry } from 'api/DataReview';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Alert from 'Components/DesignSystem/Alert/Alert';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Refresh, Cross } from 'Components/DesignSystem/Iconography/Icons';

/*
    Display-only: the BE writes every line as "<timestamp> UTC: <message>", and
    repeating that identical prefix at full body weight nine times is what made
    the panel read as a wall. The lazy `.+?` stops at the FIRST " UTC: ", so a
    message that itself mentions UTC keeps its tail intact; a line that does not
    match at all (or a future format change) falls back to rendering verbatim —
    the log content is never altered, only typeset.
*/
const LOG_LINE = /^(.+?\bUTC)\s*:\s*([\s\S]*)$/;

interface Props {
  entry: ReviewQueueEntry | null;
  onClose: () => void;
  onRefresh: () => void;
  refreshing: boolean;
}

/**
 * The single-entry log slide-over, on the DS `SidePanel` (2026-09-05, `lg`).
 * Stacks on top of the Review Queue panel — `useDismissable`'s LIFO stack
 * means Escape closes THIS layer first, then the queue (the hand-rolled
 * version had no Escape at all). The refresh icon reuses the parent drawer's
 * queue-refresh handler — there's no per-entry endpoint, the entries list is
 * the source of truth and this panel re-derives its `entry` from that list on
 * every render.
 */
const ReviewQueueLogModal: React.FC<Props> = ({ entry, onClose, onRefresh, refreshing }) => {
  if (!entry) return null;
  return (
    <SidePanel
      isOpen
      onClose={onClose}
      width="md"
      ariaLabel={`Log — ${entry.scope?.label || entry.uid}`}
      title={
        <div className="tw-min-w-0 tw-pr-3">
          <div className="tw-truncate" title={entry.scope?.label || entry.uid}>
            Log — {entry.scope?.label || entry.uid}
          </div>
          {/* Surface the queue entry uid so the user can copy it for
              cross-referencing with BE logs / support tickets. Displayed
              as-is (no hyphen stripping) so the visible string matches
              the API path/value verbatim. */}
          <div
            className="tw-text-ink-secondary tw-text-xs tw-font-normal tw-truncate tw-font-noto-sans-mono tw-mt-0.5"
            title={entry.uid}
          >
            {entry.uid}
          </div>
        </div>
      }
      tabs={[]}
      data-testid="review-queue-log-modal"
      headerActions={
        <>
          <IconButton
            size="sm"
            aria-label="Refresh logs"
            title="Refresh logs"
            data-testid="review-queue-reviewqueuelogmodal-button-1"
            disabled={refreshing}
            onClick={onRefresh}
          >
            <Refresh className={refreshing ? 'tw-animate-spin' : undefined} />
          </IconButton>
          <IconButton
            size="sm"
            aria-label="Close log"
            data-testid="review-queue-reviewqueuelogmodal-button-2"
            onClick={onClose}
          >
            <Cross size={16} />
          </IconButton>
        </>
      }
    >
      {entry.failure_reason ? (
        <div className="tw-mb-3">
          <Alert tone="danger" data-testid="review-queue-log-failure-reason">
            <strong>Failure reason:</strong> {entry.failure_reason}
          </Alert>
        </div>
      ) : null}
      {(() => {
        const lines = (entry.logs || '')
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .reverse();
        if (lines.length === 0) {
          return (
            <div className="tw-py-4 tw-text-sm tw-text-ink-secondary">
              (no log entries)
            </div>
          );
        }
        return (
          <ul className="tw-list-none tw-p-0 tw-m-0">
            {lines.map((line, idx) => {
              const m = LOG_LINE.exec(line);
              return (
                <li
                  key={idx}
                  className="tw-py-3 tw-border-b tw-border-edge last:tw-border-b-0"
                >
                  {m ? (
                    <>
                      <Typography
                        variant="caption-metadata"
                        as="div"
                        className="tw-text-ink-tertiary tw-tabular-nums"
                      >
                        {m[1]}
                      </Typography>
                      <Typography
                        variant="body-primary"
                        as="div"
                        className="tw-text-ink tw-break-words tw-mt-0.5"
                      >
                        {m[2]}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      variant="body-primary"
                      as="div"
                      className="tw-text-ink tw-break-words"
                    >
                      {line}
                    </Typography>
                  )}
                </li>
              );
            })}
          </ul>
        );
      })()}
    </SidePanel>
  );
};

export default ReviewQueueLogModal;
